import type { CheckResponse } from '@palaccount/contracts';

/**
 * The FIN-10 / FIN-11 lifecycle, client side: the same transition tables the
 * server enforces, so the status control offers ONLY legal next states (the
 * prototype's any-order dropdown is a listed defect on both screens), plus
 * the §5b due-chip arithmetic of the reminders rail.
 */

export type CheckStatus = CheckResponse['status'];

/**
 * Transition targets by current status — void and reverse have their own
 * routes and are not "next states" here.
 *
 * ERR-FIN-10-04 is answered HERE, by omission. The §14 row records the
 * prototype letting a cheque go straight from In hand to Cleared, stamping
 * the clearance date and leaving the deposit date empty; the defects table
 * offers "require the deposit step, or stamp both", and this build takes the
 * first. There is no in_hand → cleared edge, so BR-FIN-10-06's "cleared must
 * imply deposited" is a property of the graph rather than of a stamp that
 * could be missed. Pinned in `err-clusters-2-lifecycle.test.ts`, which
 * asserts 'cleared' is ABSENT — restoring the spec's text fails a test.
 */
export const NEXT_STATES: Readonly<Record<string, readonly CheckStatus[]>> = {
  // incoming
  in_hand: ['deposited', 'returned', 'cancelled'],
  deposited: ['cleared', 'bounced'],
  // outgoing
  issued: ['given', 'cancelled'],
  given: ['presented', 'bounced'],
  presented: ['cleared', 'bounced'],
  // terminal — `endorsed` is PV-01's: handed on to a supplier by a payment voucher.
  endorsed: [],
  cleared: [],
  bounced: [],
  returned: [],
  cancelled: [],
  voided: [],
};

export function nextStatesOf(status: CheckStatus): readonly CheckStatus[] {
  return NEXT_STATES[status] ?? [];
}

/** BR-FIN-11-03: the void panel exists ONLY while Issued — and vanishes for
 *  good the moment the cheque is given away. */
export function canVoid(check: Pick<CheckResponse, 'direction' | 'status'>): boolean {
  return check.direction === 'outgoing' && check.status === 'issued';
}

/**
 * BR-FIN-10-12: reversal only on a bounced cheque, one-shot.
 *
 * ERR-FIN-10-03 — "clicked Reverse cheque twice; it fades and stops
 * responding after the first press" — is one of the few FIN-10 §14 rows the
 * build KEEPS rather than repairs, because BR-FIN-10-12 requires exactly it.
 * The button reads this for `disabled` and for the faded class; the server
 * refuses a second call on `reversedAt !== null` independently, so the guard
 * is never the button's alone.
 */
export function canReverse(check: Pick<CheckResponse, 'status' | 'reversedAt'>): boolean {
  return check.status === 'bounced' && check.reversedAt === null;
}

/** BR-FIN-11-10: one real replacement, outgoing bounces only. */
export function canReissue(
  check: Pick<CheckResponse, 'direction' | 'status' | 'replacementCheckId'>,
): boolean {
  return (
    check.direction === 'outgoing' && check.status === 'bounced' && check.replacementCheckId === null
  );
}

/** "Awaiting action" (BR-FIN-10-15 / BR-FIN-12-11): in hand incoming;
 *  issued or given outgoing. */
export function awaitingAction(check: Pick<CheckResponse, 'direction' | 'status'>): boolean {
  return check.direction === 'incoming'
    ? check.status === 'in_hand'
    : check.status === 'issued' || check.status === 'given';
}

export interface DueChip {
  kind: 'overdue' | 'due_soon' | 'post_dated' | 'none';
  days: number;
}

/**
 * FIN-10 §5b, exactly:
 *   days until = cheque date − today
 *   overdue    = still awaiting action and days < 0
 *   due soon   = awaiting and 0 ≤ days ≤ the flag-due-within setting
 *   post-dated = days strictly above zero (a cheque dated TODAY is not
 *                post-dated, BR-FIN-10-14)
 */
/**
 * F-105 — `days_until = due − today`; overdue red, due within the reminder
 * window amber, later (strictly future) post-dated — a cheque due today is
 * NOT post-dated. The window is the Check-reminder-before setting the screen
 * offers as its flag control.
 */
export function dueChip(
  check: Pick<CheckResponse, 'direction' | 'status' | 'chequeDate'>,
  todayIso: string,
  flagWithinDays: number,
): DueChip {
  const days = daysBetween(todayIso, check.chequeDate);
  if (!awaitingAction(check)) return { kind: 'none', days };
  if (days < 0) return { kind: 'overdue', days };
  if (days <= flagWithinDays) return { kind: 'due_soon', days };
  if (days > 0) return { kind: 'post_dated', days };
  return { kind: 'none', days };
}

export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000);
}

/** BR-FIN-11-06: a voided or cancelled cheque dims with its number struck. */
export function isDeadRow(status: CheckStatus): boolean {
  return status === 'voided' || status === 'cancelled';
}
